import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { isString } from 'lodash';
import { BrandingService, BrandingType } from 'shared/services/branding';
import { UserPermissionService } from 'shared/services/user-permissions/user-permission.service';
import { ActionPermissionType } from 'common/common/constants/permission-type-enum';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { Subscription } from 'rxjs';
import { StateServiceWrapper } from 'shared/services/state-service-wrapper/state-service-wrapper';
import { BcAuth } from 'common/auth/auth.service';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { LogoutSubscriber } from 'shared/services/subscribers/logout-subscriber.service';
import { LoginSubscriber } from 'shared/services/subscribers/login-subscriber.service';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import { MatTooltip } from '@angular/material/tooltip';
import { bcStates } from 'common/common/states';
import { CommonAuthService } from 'common/auth/common-auth.service';
import { BcConfigService } from 'common/config/provider';
import { UserPermission as GenesysUserPermission } from 'common/store/genesys-cloud/users/models/user-permission.enum';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class NavbarComponent implements OnInit, OnDestroy {
  @ViewChild('tooltip', { static: true }) tooltip: MatTooltip;

  public username: string;
  public isLoggedIn: boolean;
  public initial;
  public canChangePassword;
  public isListRecordingsEnabled: boolean = false;
  public isUserWithMultipleAccount: boolean = false;
  public branding: {
    logo: {
      title: string;
      icon: string;
    };
    links: {
      help: string;
    }
  };
  public readonly gcUserPermission = GenesysUserPermission;
  public isBoldEnabled = this.commonAuthService.isBoldEnabled();
  public isHybrid = this.commonAuthService.isHybrid();
  private logoutSubscription: Subscription;
  private loginSubscription: Subscription;
  private brandingSubscription: Subscription;
  private confirmationPanelTooltipSubscription: Subscription;

  constructor(private rxEmitter: RxEmitter,
              private stateService: StateServiceWrapper,
              private bcAuth: BcAuth,
              private bcAnalytics: BcAnalytics,
              private loginSubscriber: LoginSubscriber,
              private logoutSubscriber: LogoutSubscriber,
              private userPermissionService: UserPermissionService,
              private changeDetector: ChangeDetectorRef,
              private brandingService: BrandingService,
              private commonAuthService: CommonAuthService,
              private configService: BcConfigService) {
  }

  ngOnInit(): void {
    this.loginSubscription = this.loginSubscriber.subscribe(() => this.getUser());
    this.logoutSubscription = this.logoutSubscriber.subscribe(() => this.clearUser());
    this.brandingSubscription = this.brandingService.currentBranding.subscribe(brandingType => this.onBrandingChanged(brandingType));

    this.confirmationPanelTooltipSubscription = this.rxEmitter.subscribe(MessageBusEvents.ShowConfirmationPanelTooltip, () => {
      this.tooltip.show(0);
      this.changeDetector.detectChanges();
      setTimeout(() => {
        this.tooltip.hide();
      }, 8000);
    });
  }

  ngOnDestroy(): void {
    this.loginSubscription?.unsubscribe();
    this.logoutSubscription?.unsubscribe();
    this.brandingSubscription?.unsubscribe();
    this.confirmationPanelTooltipSubscription?.unsubscribe();
  }

  get isGenesysEnabled(): boolean {
    return this.commonAuthService.isGenesysEnabled;
  }

  /**
   * Subscription handler that is called when the user's preferred branding has been changed.
   * @param {BrandingType} newBranding - The new branding to be displayed.
   */
  onBrandingChanged(newBranding: BrandingType) {
    switch (newBranding) {
      case BrandingType.BoldChat: {
        updateFavicon(this.configService.constructAssetUrl('images/favicon-boldchat.ico?v=1.0'));
        this.branding = {
          logo: {
            title: 'BoldChat',
            icon: 'boldchat-logo'
          },
          links: {
            help: 'https://help.boldchat.com'
          }
        };
        break;
      }
      case BrandingType.Bold360: {
        updateFavicon(this.configService.constructAssetUrl('images/favicon-bold360.png?v=3.0'));
        this.branding = {
          logo: {
            title: 'Genesys DX',
            icon: 'genesys-logo'
          },
          links: {
            help: 'https://dx.genesys.com'
          }
        };
        break;
      }
      default: {
        throw new Error(`The specified branding type (${newBranding}) is not supported by the NavBar!`);
      }
    }

    function updateFavicon(newFaviconUrl) {
      if (!newFaviconUrl) {
        throw new Error('Please specify the newFaviconUrl!');
      }

      const link: HTMLLinkElement = window.document.querySelector('link[rel*="icon"]') || window.document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';
      link.href = newFaviconUrl;
      window.document.getElementsByTagName('head')[0].appendChild(link);
    }
  }

  async getUser() {
    this.username = await this.commonAuthService.getUserName();
    this.isLoggedIn = await this.getLoggedInState();
    this.isUserWithMultipleAccount = await this.commonAuthService.isMultiAccount();
    this.canChangePassword = this.commonAuthService.canChangePassword();
    this.initial = this.getInitials(this.username);
    this.isListRecordingsEnabled = await this.commonAuthService.isBoldEnabled() && (await this.userPermissionService.hasPermissionForAsync(ActionPermissionType.RC_RECORDINGS_ALL) ||
      await this.userPermissionService.hasPermissionForAsync(ActionPermissionType.RC_RECORDINGS_OWN));
  }

  private async getLoggedInState(): Promise<boolean> {
    if(await this.commonAuthService.isBoldEnabled()){
      return Promise.resolve(this.commonAuthService.isLoggedIn())
    }
    return this.commonAuthService.isLoggedIn() && await this.commonAuthService.hasDigitalDesktopPermission();
  }

  clearUser() {
    this.username = null;
    this.isLoggedIn = null;
  }

  async logout(): Promise<void> {
    this.bcAnalytics.eventTrack('Logout', { from: 'NavBar' });
    await this.commonAuthService.logout();
  }

  async switchAccount(): Promise<void> {
    await this.bcAuth.switchAccount();
  }

  goToRecordingsPage() {
    this.stateService.go(bcStates.recordingsList.url);
    this.bcAnalytics.eventTrack('OpenRCRecordings', { from: 'NavBar' });
  }

  getInitials(name) {
    if (name && isString(name)) {
      const words = name.toUpperCase().split(' ');
      let initial = '';

      words.forEach(value => {
        initial += value[0];
      });

      return initial;
    }

    return null;
  }

  openChangePassword() {
    this.bcAuth.getIdentityLoginConfiguration()
      .then(identityLoginConfiguration => {
        this.openUrl(identityLoginConfiguration.changePasswordUrl);
      });
    this.bcAnalytics.eventTrack('OpenChangePasswordPanel', { from: 'NavBar' });
  }

  openSettingsMenu() {
    this.rxEmitter.send(MessageBusEvents.OpenSettingsMenu, null);
    this.bcAnalytics.eventTrack('openSettingsMenu', { from: 'NavBar' });
  }

  openUrl(pageUrl) {
    window.open(pageUrl);
  }

  mailToHref(mailTo) {
    window.location.href = mailTo;
  }
}
